import React from 'react';

import { Card } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/utils/cn';
import { formatCurrency } from 'utils';

import { formatFullCurrency, signColorClass, trendArrow } from './format';
import { GlossaryKey } from './glossary';
import { GlossaryLabel } from './GlossaryInfoButton';

// Shared reporting KPI primitives: the Summary, Holdings and Income stat rows all render
// through HeroStatCard so they stay visually identical.

// Matches the account page's HouseholdAccountsSummaryCard labels (10px tracked uppercase).
export const STAT_LABEL_CLASS = 'text-cocoa-500 text-[10px] font-semibold tracking-wider uppercase';

// Reveals the exact figure on hover — the displayed value is compact/rounded.
export const ValueTooltip = ({ full, children }: { full: string; children: React.ReactElement }) => (
    <Tooltip>
        <TooltipTrigger asChild>{children}</TooltipTrigger>
        <TooltipContent>{full}</TooltipContent>
    </Tooltip>
);

// A whole-dollar amount with the exact figure on hover — how the reporting tables show money.
export const WholeDollars = ({ value }: { value: number | string }) => (
    <ValueTooltip full={formatFullCurrency(value)}>
        <span>{formatCurrency(value)}</span>
    </ValueTooltip>
);

// Explains how a figure is calculated on hover; renders children untouched when
// there is no explanation. The copy lives next to the math it describes
// (e.g. household/holdings.ts) so the two are updated together.
export const CalculationTooltip = ({ text, children }: { text?: string; children: React.ReactElement }) =>
    text ? (
        <Tooltip>
            <TooltipTrigger asChild>{children}</TooltipTrigger>
            <TooltipContent className="max-w-xs">{text}</TooltipContent>
        </Tooltip>
    ) : (
        children
    );

export interface HeroStatProps {
    label: string;
    value: string;
    // Exact value revealed on hover.
    tooltip: string;
    // How the figure is calculated; shown as a hint tooltip on the label.
    calculation?: string;
    // GLOSSARY_TERMS key; adds the info icon that opens the glossary on this metric.
    glossaryKey?: GlossaryKey;
    // Drives gain/loss color and the optional trend arrow.
    signedValue?: number;
    showArrow?: boolean;
}

// One stat — label and value — without a card, so it can share a card with other stats
// (HeroStatCard).
const HeroStat = ({ label, value, tooltip, calculation, glossaryKey, signedValue, showArrow }: HeroStatProps) => {
    const color = signColorClass(signedValue) ?? 'text-cocoa-1000';
    const arrow = trendArrow(signedValue);

    return (
        <div className="flex flex-col gap-2">
            <CalculationTooltip text={calculation}>
                {/* The info icon goes inside the label, so the value stays the label's next sibling. */}
                <GlossaryLabel className={STAT_LABEL_CLASS} label={label} glossaryKey={glossaryKey} />
            </CalculationTooltip>
            <div className="flex items-baseline gap-1.5">
                {showArrow && arrow ? <span className={cn('text-xs', color)}>{arrow}</span> : null}
                <ValueTooltip full={tooltip}>
                    <span className={cn('text-2xl leading-none font-semibold tabular-nums', color)}>{value}</span>
                </ValueTooltip>
            </div>
        </div>
    );
};

interface HeroStatCardProps {
    stats: HeroStatProps[];
    isLoading?: boolean;
}

// Columns by stat count, so a row never breaks into an orphan (no 4 + 1). Container queries on
// the card, not viewport breakpoints: the sidebar and section nav decide its width. Each step
// leaves every stat at least 220px. Other counts fall back to as many 220px columns as fit.
const GRID_COLUMNS: Record<number, string> = {
    3: 'grid-cols-1 @min-[660px]:grid-cols-3',
    4: 'grid-cols-2 @min-[880px]:grid-cols-4',
    5: 'grid-cols-2 @min-[660px]:grid-cols-3 @min-[1100px]:grid-cols-5',
};
const FALLBACK_GRID_COLUMNS = 'grid-cols-[repeat(auto-fit,minmax(220px,1fr))]';

// Every stat draws a right and a bottom rule (border-border, like HouseholdAccountsSummaryCard's
// divider). The grid extends 1px past the card's right and bottom edges, clipped by
// overflow-hidden, so the last column loses its right rule and the last row its bottom rule.
// Rules hang off the cell before a gap rather than the one after it, so a wrapped row that is not
// full still gets a full-width line above it; the last stat drops its right rule so an unfilled
// row ends without a dangling vertical line.
const STAT_CELL_CLASS = 'border-border border-r border-b p-4 last:border-r-0 lg:p-6';

// Several stats in one card divided by rules — the Summary KPI design. While loading, each stat
// shows label and value bars at their own sizes, so the card keeps its height when the figures land.
export const HeroStatCard = ({ stats, isLoading }: HeroStatCardProps) => (
    <Card className="border-border @container overflow-hidden rounded-2xl bg-white">
        <div className={cn('-mr-px -mb-px grid', GRID_COLUMNS[stats.length] ?? FALLBACK_GRID_COLUMNS)}>
            {stats.map((stat, i) => (
                // A fixed list that never reorders; the index keeps duplicate labels from colliding.
                <div key={i} className={STAT_CELL_CLASS}>
                    {isLoading ? (
                        <div className="flex flex-col gap-2">
                            <div className="bg-cocoa-100 h-2.5 w-24 animate-pulse rounded" />
                            <div className="bg-cocoa-100 h-6 w-32 animate-pulse rounded" />
                        </div>
                    ) : (
                        <HeroStat {...stat} />
                    )}
                </div>
            ))}
        </div>
    </Card>
);
